import { ExplorerBookmarkState } from "@novorender/data-js-api";
import { useCallback } from "react";

import { useCreateSharelinkMutation } from "apis/dataV2/dataV2Api";
import { useAppSelector } from "app/redux-store-interactions";
import { useCreateBookmark } from "features/bookmarks/useCreateBookmark";
import { useSceneId } from "hooks/useSceneId";
import { selectCurrentProjectVersion } from "slices/explorer/selectors";

export class ShareLinkClipboardError extends Error {
    readonly link: string;
    constructor(link: string, cause?: unknown) {
        super("Failed to write share link to clipboard");
        this.name = "ShareLinkClipboardError";
        this.link = link;
        this.cause = cause;
    }
}

export function useShareLink({ returnLink = false } = {}) {
    const createBookmark = useCreateBookmark();
    const [createSharelink] = useCreateSharelinkMutation();
    const sceneId = useSceneId();
    const projectVersion = useAppSelector(selectCurrentProjectVersion);

    return useCallback(
        async (
            explorerStateOverwrite: Partial<ExplorerBookmarkState> = { forms: undefined },
            // leaving unused for now as it will be used again when reimplementing stable sharelinks
            _abortSignal: AbortSignal,
        ) => {
            const id = window.crypto.randomUUID();
            const link = `${window.location.origin}${window.location.pathname}?bookmarkId=${id}`;

            const create = async () => {
                const bm = createBookmark(undefined, explorerStateOverwrite);
                return createSharelink({
                    projectId: sceneId,
                    bookmark: { ...bm, id, name: id },
                    projectVersion: projectVersion ?? "",
                }).unwrap();
            };

            if (returnLink) {
                await create();
                return link;
            }

            const blob = new Blob([link], {
                type: "text/plain",
            });
            let bookmarkSaved = false;

            try {
                // Safari treats user activation differently:
                // https://bugs.webkit.org/show_bug.cgi?id=222262.
                await navigator.clipboard.write([
                    new ClipboardItem({
                        "text/plain": (async () => {
                            await create();
                            bookmarkSaved = true;
                            return blob;
                        })(),
                    }),
                ]);
            } catch (e) {
                if (!bookmarkSaved) {
                    throw e;
                }

                try {
                    await navigator.clipboard.write([
                        new ClipboardItem({
                            [blob.type]: blob,
                        }),
                    ]);
                } catch (clipboardError) {
                    throw new ShareLinkClipboardError(link, clipboardError);
                }
            }

            return true;
        },
        [createBookmark, createSharelink, projectVersion, returnLink, sceneId],
    );
}
